// src/costPerLetter.js
// ─────────────────────────────────────────────────────────────
// Cost per letter (10/10/26) — pure: no React, no network. The screen is
// src/views/CostPerLetter.jsx (a section of the Expenses page).
//
// Reece: "compare things like cost per letter over various periods of time.
// That would include: data purchased, paper, envelopes, ink and toner,
// postage … it is going to be pretty shocking looking at how much I was
// spending per letter before compared to now. Also include an averaged-out
// cost of the business cards. When looking at letters sent out, I likely
// stopped sending those out completely around late August. And for
// reference the mailhouse purchase that was done for roughly $2,400, that
// was for 1500 letters, so for that, it was about 1.60 per."
// "Those" = the BUSINESS CARDS (10/10, Reece: "I was saying that I stopped
// mailing business cards in envelopes in late August") — the letters went
// on through September; only the card inside the envelope stopped
// (cardsUntil, default CARDS_UNTIL_DEFAULT = Aug 31, 2026).
//
// And (10/10): "the master logs are, for some reason, not accurate … if
// you look at the money that was spent on stamps, it is impossible for that
// to outpace the letters based on the 82-cent minimum. There are a number of
// Stamp.com purchases. Those were based off a 78-cent minimum, but there's
// no chance that I could have possibly sent out 70 cents per letter in
// August." → the STAMP CHECK (below), the default basis.
//
//   LETTERS — one letter per lead per LOCAL day (every `mailings[]` entry
//     with a date; the same letter logged twice on one lead the same day —
//     a sync's copy under another id — counts once). Dates: sentAt, then a
//     dateSent that is a date ("2026-07-08", "7/8/2026"), then a date INSIDE
//     the text ("2026-07-18 (est.)", "~2026-06-11" — "~" / "est." / "approx"
//     mark it approximate; a piped "<as typed> | 2026-07-10" takes its last
//     date; a "returned …" date is never the send date), then a month
//     ("UNKNOWN (July 2026 handwritten)",
//     "2026-04 (Campaign 3)" — that month, read as its 15th). Future-dated /
//     undated ones are counted but left out. Split by method: Mailhouse (a
//     Marketing project, a batch / campaign / note that names the mailhouse,
//     or the lead's "mailhouse-<that day>" tag) / Handwritten / Printed /
//     Mixed / Other. A pipeline file's own "mail" contact entry (New Call's
//     letter on a file with no lead) counts too, once — the Letters tracker's
//     rule. PLUS letter counts typed onto expenses: the notes line "Letters:
//     1,500" (parseLettersLine / withLettersLine) for a drop that isn't
//     logged as mailings. A mailhouse charge's other invoices (same vendor,
//     ±30 days, no count of their own — the AlphaGraphics 6/26 + 7/7
//     charges) are ONE drop with it; its letters are spread over its
//     charges' dates in proportion to their amounts. Never counted twice
//     (matchDrops): a drop is "already logged" — and adds nothing — when a
//     batch / campaign / project the charge NAMES is logged, or (a mailhouse
//     drop) mailhouse mailings are logged from 7 days before its charges to
//     30 after (however many batches they're split into), or one group in
//     that window has roughly its count (±10%, at least ±10), or the letters
//     logged on ONE day in that window add up to roughly its count.
//   COSTS — from expenseItemParts (src/expenseItems.js), every category: the
//     Marketing parts AND the same item types filed elsewhere (a hand-entered
//     PropStream under Software is Data, ink under Office/Equipment is Ink).
//     Components: Data, Paper, Cardstock (or folded into Paper), Envelopes,
//     Ink & Toner, Postage (incl. the mailhouse — expenseItems types
//     AlphaGraphics as Postage), and Business cards AVERAGED: all-time card
//     spend ÷ cards bought (the Items lines' counts) × cards per letter ×
//     letters — never more than was spent: when the letters need more cards
//     than were ever bought, the card spend is spread over all the letters
//     instead (`capped`). Cards only go in the letters mailed on or before
//     `cardsUntil` (default Aug 31, 2026; empty = every letter): the cap,
//     the spread and every period's card cost use those letters only.
//     Refunds net out (a negative part). Labels, pens, printing and the
//     mailing machine are not part of it.
//   STAMP CHECK (basis "stamps", the default; "logged" = as logged, postage
//     as bought) — a stamped letter needs a stamp, so the letters logged in
//     a month can't outrun the stamps bought by then. Stamps bought = every
//     Postage part that is NOT the mailhouse (its postage is in its invoice;
//     its letters are the Mailhouse-method ones) nor parcel postage (a
//     charge that says Priority Mail / Click-N-Ship / tracking … and no
//     stamps): the Items line's stamp count ("Postage $427.23 (471 stamps)"
//     — the receipt's letter stamps; add-on / extra-ounce stamps and parcels
//     aren't letters), else the amount ÷ the stamp price ON ITS DATE,
//     rounded down — USPS and every other store 78¢ before 7/13/2026 and 82¢
//     from then (Reece's receipts: "US Flag Coil/100 $78.00" through 7/07,
//     "$82.00" from 7/13; Fry's "@ $15.60" a book of 20 in April, "@ $16.40"
//     in September; the current price is a setting), Stamps.com 78¢
//     (setting); a refund takes its stamps back. Month by month, all time,
//     oldest stamps first (FIFO cost layers): the stamps on hand + the
//     month's purchases back the month's stamped letters (every letter but
//     the mailhouse's); the rest are "not backed" and left out of the letter
//     count (they never take a later month's stamps). Within a month the
//     letters are backed in day order — the first logged first — so a range
//     that cuts a month gets the backed letters (and their stamps' cost)
//     that fall inside it. Postage = what the backed letters' stamps cost,
//     in the month they were used, + the mailhouse's invoices in the month
//     its letters went out (mailhousePlacement: a mailhouse's charges within
//     30 days are one drop, spread over the Mailhouse letters logged from 7
//     days before its charges to 30 after; as bought when none are); stamps
//     left at the end are "on hand" (in no month's figure).
//   SMOOTHED — supplies (paper, cardstock, envelopes, ink) spread by
//     consumption: the span's supply spend ÷ the span's letters, so a bulk
//     buy doesn't make one month look awful and the next free. Postage and
//     data stay in the period they were bought (postage: used, when checked
//     against stamps).
//   EXACT — every amount is integer cents; a per-letter figure is one
//     division rounded to the cent, and a period's components are rounded
//     so they add up to its total exactly (largest remainder).
// ─────────────────────────────────────────────────────────────

import { expenseItemParts, expenseVendorGroup, NO_VENDOR, parseItemsLine, formatItemsLine, hasItemsLine, withItemsLine, replaceItemsLine, oneItemLine, POSTAGE_KINDS } from './expenseItems';
import { localDay } from './regression';

var COMPONENTS=[
  {key:"Data",label:"Data",type:"Data",supply:false},
  {key:"Paper",label:"Paper",type:"Paper",supply:true},
  {key:"Cardstock",label:"Cardstock",type:"Cardstock",supply:true},
  {key:"Envelopes",label:"Envelopes",type:"Envelopes",supply:true},
  {key:"Ink & Toner",label:"Ink & Toner",type:"Ink & Toner",supply:true},
  {key:"Postage",label:"Postage",type:"Postage",supply:false},
  {key:"Business Cards",label:"Business cards",type:"Business Cards",supply:true,averaged:true}
];
var COMP_OF_TYPE={};COMPONENTS.forEach(function(c){COMP_OF_TYPE[c.type]=c.key;});
var COMP_INFO={};COMPONENTS.forEach(function(c){COMP_INFO[c.key]=c;});
var METHODS=["Handwritten","Printed","Mixed","Mailhouse","Other"];
var HANDWRITTEN_LETTER="Handwritten Letter";   // New Call's letter campaign (src/views/Letters.jsx)
var DROP_WINDOW_DAYS=30;                       // a drop's other invoices / its logged mailings (after its charges)
var DROP_LEAD_DAYS=7;                          // … and its logged mailings before its charges
var NAMED_WINDOW_DAYS=60;                      // a batch the charge names
var STOP_MIN_LETTERS=10;                      // a month with fewer is not a mailing month (the last-mailing-month read)
var CARDS_UNTIL_DEFAULT="2026-08-31";          // business cards stopped going in the envelopes (Reece, 10/10)
var STAMP_PRICE_USPS=82;                       // cents a stamp: USPS, the post office, every other store — from USPS_PRICE_FROM (the current price; a setting)
var STAMP_PRICE_USPS_BEFORE=78;                // … bought before it (his receipts: "US Flag Coil/100 $78.00" through 7/07/26)
var USPS_PRICE_FROM="2026-07-13";              // the first 82¢ receipt ("US Flag Coil/100 $82.00")
var STAMP_PRICE_STAMPSCOM=78;                  // cents a stamp: Stamps.com
// Parcel postage — never letter stamps: a postage charge whose vendor /
// description says so and names no stamps (a mixed "stamps + Priority Mail"
// receipt needs its stamp count on the Items line).
var PARCEL_RE=/priority mail|\bpm express\b|\bpme\b|click[- ]?n[- ]?ship|\btracking\b|\bparcels?\b|shipping label|certified mail|ground advantage/i;
var STAMP_WORDS_RE=/\bstamps?\b|\bcoils?\b|booklets?|forever/i;
var STAMPS_COM="Stamps.com";                   // expenseItems' vendor group
var SAME_LETTER_MS=10000;                      // a file's copy of its lead's letter (Letters.jsx)
var LETTERS_PREFIX="Letters: ";
var STAMP_START="Imported from";               // expenseImport's stamp lines stay last
var MONTHS_SHORT=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
var MONTHS_LONG=["January","February","March","April","May","June","July","August","September","October","November","December"];
var MONTH_WORDS=["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];

// ── Small pieces ─────────────────────────────────────────────

function oneLine(s){return String(s==null?"":s).replace(/\s+/g," ").trim();}
function pad2(n){return(n<10?"0":"")+n;}
function cents(n){return Math.round((Number(n)||0)*100);}
function todayLocal(){var d=new Date();return d.getFullYear()+"-"+pad2(d.getMonth()+1)+"-"+pad2(d.getDate());}
function isDay(s){return/^\d{4}-\d{2}-\d{2}$/.test(String(s||""))&&localDay(s)===s;}
// Whole days since 1970 for a "YYYY-MM-DD" (calendar arithmetic only — no time zone).
function dayNum(day){var m=/^(\d{4})-(\d{2})-(\d{2})$/.exec(day||"");return m?Date.UTC(+m[1],+m[2]-1,+m[3])/864e5:NaN;}
function dayFromNum(n){var d=new Date(n*864e5);return d.getUTCFullYear()+"-"+pad2(d.getUTCMonth()+1)+"-"+pad2(d.getUTCDate());}
function addDays(day,n){var t=dayNum(day);return isNaN(t)?null:dayFromNum(t+n);}
function daysApart(a,b){return Math.abs(dayNum(a)-dayNum(b));}
function fmtInt(n){return String(Math.round(Number(n)||0)).replace(/\B(?=(\d{3})+(?!\d))/g,",");}
function fmtDay(day){if(!isDay(day))return day||"";return MONTHS_SHORT[+day.slice(5,7)-1]+" "+(+day.slice(8,10))+", "+day.slice(0,4);}
function fmtMoneyCents(c){
  c=Math.round(Number(c)||0);
  var s=String(Math.floor(Math.abs(c)/100)).replace(/\B(?=(\d{3})+(?!\d))/g,",")+"."+pad2(Math.abs(c)%100);
  return(c<0?"-":"")+"$"+s;
}

// Integer division rounded half up (toward +∞), exact for the sizes here.
function divRound(num,den){
  if(!den)return 0;
  if(den<0){num=-num;den=-den;}
  var q=Math.floor(num/den);var r=num-q*den;
  if(r<0){q-=1;r+=den;}else if(r>=den){q+=1;r-=den;}
  return 2*r>=den?q+1:q;
}
// Split integer `total` in proportion to non-negative integer `weights` —
// the parts sum EXACTLY to the total (largest remainder; ties → the bigger
// weight, then the earlier one). All-zero weights → all zeros.
function allocate(total,weights){
  var w=(weights||[]).map(function(x){return Math.max(0,Math.round(Number(x)||0));});
  var W=w.reduce(function(s,x){return s+x;},0);
  if(!W||!total)return w.map(function(){return 0;});
  var sign=total<0?-1:1;var T=Math.abs(total);
  var base=w.map(function(x){return Math.floor(T*x/W);});
  var rem=w.map(function(x,i){return T*x-base[i]*W;});
  var left=T-base.reduce(function(s,x){return s+x;},0);
  var order=w.map(function(_,i){return i;}).sort(function(a,b){return rem[b]-rem[a]||w[b]-w[a]||a-b;});
  for(var k=0;k<left;k++)base[order[k%order.length]]+=1;
  return base.map(function(x){return sign*x;});
}
// nums (integer cents) ÷ den (letters) → integer cents each, rounded so they
// add up to divRound(sum, den) exactly: the per-letter components of a
// period always sum to its per-letter total.
function apportion(nums,den){
  if(!den)return nums.map(function(){return null;});
  var floors=nums.map(function(n){return Math.floor(n/den);});
  var rems=nums.map(function(n,i){return n-floors[i]*den;});
  var sum=nums.reduce(function(s,n){return s+n;},0);
  var left=divRound(sum,den)-floors.reduce(function(s,x){return s+x;},0);
  var order=nums.map(function(_,i){return i;}).sort(function(a,b){return rems[b]-rems[a]||Math.abs(nums[b])-Math.abs(nums[a])||a-b;});
  for(var k=0;k<left&&k<order.length;k++)floors[order[k]]+=1;
  return floors;
}

// ── The "Letters: N" notes line ──────────────────────────────

// A count line is "Letters:" + a NUMBER ("Letters: 1,500", "letters:1500",
// "Letters: 1,500 (AlphaGraphics drop)") — a comment that merely starts with
// "Letters:" ("Letters: printed in-house …") is ordinary text, never touched.
var LETTERS_LINE_RE=/^\s*letters?\s*:\s*(\d{1,3}(?:,\d{3})+|\d+)(.*)$/i;
function isLettersLine(line){return LETTERS_LINE_RE.test(String(line||""));}
function lettersCount(s){var n=parseInt(String(s).replace(/,/g,""),10);return isFinite(n)&&n>0&&n<=10000000?n:null;}
// notes (or one line) → the letter count it carries, or null.
function parseLettersLine(notes){
  var n=null;
  String(notes||"").split("\n").some(function(l){var m=LETTERS_LINE_RE.exec(l);if(m){n=lettersCount(m[1]);return true;}return false;});
  return n;
}
function formatLettersLine(n){
  n=Math.round(Number(n)||0);
  return n>0?LETTERS_PREFIX+fmtInt(n):"";
}
// Put the count on (or take it off) the notes — ONE line, replaced in place
// (any remark after the number stays: "Letters: 1,500 (2 invoices)" →
// "Letters: 1,400 (2 invoices)"; taking the count off keeps the remark as a
// line of its own); a new one goes above the import stamp lines (else at the
// end). Every other line is kept exactly as it was.
function withLettersLine(notes,n){
  var line=formatLettersLine(n);
  var lines=String(notes||"")?String(notes).split("\n"):[];
  var at=lines.findIndex(isLettersLine);
  if(at>=0){
    var out=[];
    lines.forEach(function(l,i){
      if(i===at){var rest=LETTERS_LINE_RE.exec(l)[2]||"";if(line)out.push(line+rest);else if(rest.trim())out.push(rest.trim());}
      else if(!isLettersLine(l))out.push(l);
    });
    return out.join("\n");
  }
  if(!line)return String(notes||"");
  var stamp=lines.findIndex(function(l){return String(l).trim().indexOf(STAMP_START)===0;});
  if(stamp<0)lines.push(line);else lines.splice(stamp,0,line);
  return lines.join("\n").replace(/^\n+/,"");
}

// ── Periods ──────────────────────────────────────────────────

function monthKey(day){return day.slice(0,7);}
function quarterKey(day){return day.slice(0,4)+"-Q"+(Math.floor((+day.slice(5,7)-1)/3)+1);}
function periodKeyOf(day,kind){return kind==="quarter"?quarterKey(day):kind==="day"?day:monthKey(day);}
function monthEnd(key){var y=+key.slice(0,4),m=+key.slice(5,7);return key+"-"+pad2(new Date(Date.UTC(y,m,0)).getUTCDate());}
function periodBounds(key){
  var q=/^(\d{4})-Q([1-4])$/.exec(key);
  if(q){var sm=(+q[2]-1)*3+1;return{from:q[1]+"-"+pad2(sm)+"-01",to:monthEnd(q[1]+"-"+pad2(sm+2))};}
  if(/^\d{4}-\d{2}$/.test(key))return{from:key+"-01",to:monthEnd(key)};
  return{from:key,to:key};
}
function periodLabel(key,short){
  var q=/^(\d{4})-Q([1-4])$/.exec(key);
  if(q)return"Q"+q[2]+(short?" '"+q[1].slice(2):" "+q[1]);
  if(/^\d{4}-\d{2}$/.test(key))return MONTHS_SHORT[+key.slice(5,7)-1]+(short?"":" "+key.slice(0,4));
  return fmtDay(key);
}
// Every period from `from` to `to` (days), oldest first; the first / last are
// cut to the range ({from, to} are the part of the period inside it).
function periodsBetween(from,to,kind){
  if(!isDay(from)||!isDay(to)||from>to)return[];
  var out=[];var cur=from;
  while(cur<=to&&out.length<400){
    var key=periodKeyOf(cur,kind);var b=periodBounds(key);
    out.push({key:key,label:periodLabel(key),short:periodLabel(key,true),from:b.from<from?from:b.from,to:b.to>to?to:b.to,full:b});
    cur=addDays(b.to,1);
  }
  return out;
}
var inRange=function(day,r){return!!day&&(!r||((!r.from||day>=r.from)&&(!r.to||day<=r.to)));};

// ── Letters ──────────────────────────────────────────────────

var MAILHOUSE_WORDS_RE=/mail ?house|alpha ?graphics|presort|letter ?shop/i;
function hasTag(lead,tag){var t=(lead&&lead.tags)||[];for(var i=0;i<t.length;i++)if(String(t[i]||"").toLowerCase()===tag)return true;return false;}
// How a letter went out. A mailhouse letter is often logged with the method
// of the letter itself ("Handwritten") — its batch / campaign / note says
// mailhouse ("Mailhouse Batch 3 (7/10)", "MAILHOUSE_Batch3 (ships 7/10)",
// "Scheduled mailhouse drop …"), or the lead carries "mailhouse-<that day>".
function methodOf(m,lead,day){
  if(m&&m.projectId)return"Mailhouse";
  if(m&&MAILHOUSE_WORDS_RE.test(oneLine(m.batch)+" | "+oneLine(m.campaign)+" | "+oneLine(m.notes)))return"Mailhouse";
  if(lead&&day&&hasTag(lead,"mailhouse-"+day))return"Mailhouse";
  var s=oneLine(m&&m.method).toLowerCase();
  if(/^hand/.test(s))return"Handwritten";
  if(/^print/.test(s))return"Printed";
  if(/^mix/.test(s))return"Mixed";
  if(MAILHOUSE_WORDS_RE.test(s))return"Mailhouse";
  return"Other";
}
var MONTH_WORD_RE=/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?,?\s+(20\d{2})\b/i;
var ISO_IN_RE=/(?:^|[^\d])(20\d{2})-(\d{1,2})-(\d{1,2})(?!\d)/;
var MDY_IN_RE=/(?:^|[^\d/])(\d{1,2})\/(\d{1,2})\/(20\d{2})(?!\d)/;
var YM_IN_RE=/(?:^|[^\d-])(20\d{2})-(\d{2})(?![\d-])/;
var APPROX_RE=/^\s*~|\best(?:\.|,|imated|\b)|approx|week of/i;
var RETURNED_RE=/return/i;
function dayIn(seg){
  var r=ISO_IN_RE.exec(seg);
  if(r){var d=localDay(r[1]+"-"+pad2(+r[2])+"-"+pad2(+r[3]));if(d)return d;}
  r=MDY_IN_RE.exec(seg);
  if(r){var d2=localDay(r[3]+"-"+pad2(+r[1])+"-"+pad2(+r[2]));if(d2)return d2;}
  return"";
}
// The LOCAL day a mailing went out: sentAt (the real instant) first, then a
// dateSent that is a date ("2026-07-08", "7/8/2026"), then a date INSIDE its
// text — "2026-07-18 (est.)", "~2026-06-11" (`approx` when that part says ~
// / est. / approx / week of; a "returned …" date is when it came back, never
// used). A piped "<as typed> | <date>" (a sync kept the original text and
// added the date it settled on: "7/10/2026 | 2026-07-10", "~2026 |
// 2026-07-10", and "5/20/2026 | 2026-07-10" on a batch that "ships 7/10")
// takes its LAST date. Then a month: named ("UNKNOWN (July 2026
// handwritten)", "~May 2026") or "2026-04 (Campaign 3)" — that month's 15th,
// marked monthOnly. A year alone is no date.
function mailingDay(m){
  var d=localDay(m&&m.sentAt)||localDay(m&&m.dateSent);
  if(d)return{day:d,monthOnly:false,approx:false};
  var s=String((m&&m.dateSent)||"");
  if(!s.trim())return null;
  var segs=s.split("|").filter(function(x){return!RETURNED_RE.test(x);});
  for(var i=segs.length-1;i>=0;i--){var x=dayIn(segs[i]);if(x)return{day:x,monthOnly:false,approx:APPROX_RE.test(segs[i])};}
  var rest=segs.join("|");
  var t=MONTH_WORD_RE.exec(rest);
  if(t)return{day:t[2]+"-"+pad2(MONTH_WORDS.indexOf(t[1].toLowerCase().slice(0,3))+1)+"-15",monthOnly:true,approx:false};
  var y=YM_IN_RE.exec(rest);
  if(y&&+y[2]>=1&&+y[2]<=12)return{day:y[1]+"-"+y[2]+"-15",monthOnly:true,approx:false};
  return null;
}
function leadOwnerOf(l){
  var t=(l&&l.tags)||[];
  for(var i=0;i<t.length;i++){var m=/^owner:(reece|max)$/.exec(String(t[i]||""));if(m)return m[1];}
  return null;
}
// Expenses' own rule (myLead): Max's are his, everything else is Reece's.
function ownsLead(l,owner){var o=leadOwnerOf(l);return owner==="max"?o==="max":o!=="max";}
// What a mailing was logged as — the unit a drop is matched against.
function groupOf(m,day,meth){
  if(m.projectId)return{key:"project:"+m.projectId,label:oneLine(m.campaign||m.batch)||"Mailhouse project"};
  if(m.campaign===HANDWRITTEN_LETTER)return{key:"letters",label:"Handwritten letters (New Call)"};
  var b=oneLine(m.batch);if(b)return{key:"batch:"+b.toLowerCase(),label:b};
  var c=oneLine(m.campaign);if(c)return{key:"campaign:"+c.toLowerCase(),label:c};
  return{key:"day:"+day+"|"+meth,label:meth+" mailings logged "+fmtDay(day)};
}
// When one lead has two records of the same letter, the one that says more
// about how it went out is kept (a mailhouse record over a bare copy; then
// one with a method typed on it over one without).
var METHOD_RANK={Mailhouse:4,Handwritten:3,Printed:3,Mixed:3,Other:0};
function recordRank(meth,m){return METHOD_RANK[meth]*2+(oneLine(m&&m.method)?1:0);}
// mktgLeads (+ opts.loans) → every letter as {day, method, group}. ONE letter
// per lead per day: a second record on the same lead and day (another sync's
// copy under a different id) is the same letter (`sameDay` counts them); a
// month-only record collapses only with another month-only one of that month.
// A mailing id seen twice counts once; a file's "mail" entry that is a copy
// of its lead's letter (same mailingId, or — older entries — within 10 s of
// it) is skipped, as the Letters tracker does.
function mailingEvents(mktgLeads,opts){
  opts=opts||{};
  var today=opts.today||todayLocal();
  var events=[];var undated=0,future=0,monthOnly=0,approx=0,sameDay=0;
  var ids=new Set();var letterTimes=new Map();
  (mktgLeads||[]).forEach(function(l){
    if(!l||(opts.owner&&!ownsLead(l,opts.owner)))return;
    var kept=null;   // "day" | "M<month>" → this lead's event there
    (l.mailings||[]).forEach(function(m){
      if(!m)return;
      if(m.id){if(ids.has(m.id))return;ids.add(m.id);}
      if(m.campaign===HANDWRITTEN_LETTER&&m.sentAt){var t=Date.parse(m.sentAt);if(!isNaN(t)){var a=letterTimes.get(l.id);if(!a){a=[];letterTimes.set(l.id,a);}a.push(t);}}
      var d=mailingDay(m);
      if(!d){undated++;return;}
      if(d.day>today){future++;return;}
      var meth=methodOf(m,l,d.day);
      var g=groupOf(m,d.day,meth);
      var slot=d.monthOnly?"M"+monthKey(d.day):d.day;
      if(!kept)kept=new Map();
      var prev=kept.get(slot);
      var rank=recordRank(meth,m);
      if(prev){
        sameDay++;
        if(rank>prev.rank){prev.method=meth;prev.group=g.key;prev.groupLabel=g.label;prev.rank=rank;}
        return;
      }
      if(d.monthOnly)monthOnly++;
      if(d.approx)approx++;
      var e={day:d.day,method:meth,group:g.key,groupLabel:g.label,monthOnly:d.monthOnly,approx:d.approx,n:1,rank:rank};
      kept.set(slot,e);events.push(e);
    });
  });
  (opts.loans||[]).forEach(function(f){
    if(!f||(opts.owner&&(f.owner||"reece")!==opts.owner))return;
    var near=letterTimes.get(f.sourceLeadId)||[];
    (f.contactLog||[]).forEach(function(e){
      if(!e||e.type!=="mail"||!e.at)return;
      if(e.mailingId){if(ids.has(e.mailingId))return;ids.add(e.mailingId);}
      else{var t=Date.parse(e.at);if(near.some(function(x){return Math.abs(x-t)<=SAME_LETTER_MS;}))return;}
      var day=localDay(e.at);
      if(!day){undated++;return;}
      if(day>today){future++;return;}
      events.push({day:day,method:"Handwritten",group:"letters",groupLabel:"Handwritten letters (New Call)",monthOnly:false,approx:false,n:1});
    });
  });
  return{events:events,undated:undated,future:future,monthOnly:monthOnly,approx:approx,sameDay:sameDay};
}
// Events → what they were logged as: {key, label, count, from, to, mh (the
// mailhouse letters in it), days: Map(day → {n, mh})}.
function mailingGroups(events){
  var m=new Map();
  events.forEach(function(e){
    var g=m.get(e.group);
    if(!g){g={key:e.group,label:e.groupLabel,count:0,mh:0,from:e.day,to:e.day,days:new Map()};m.set(e.group,g);}
    var isMh=e.method==="Mailhouse"?e.n:0;
    g.count+=e.n;g.mh+=isMh;if(e.day<g.from)g.from=e.day;if(e.day>g.to)g.to=e.day;
    var x=g.days.get(e.day);if(!x){x={n:0,mh:0};g.days.set(e.day,x);}
    x.n+=e.n;x.mh+=isMh;
  });
  return Array.from(m.values());
}

// ── Letter counts typed onto expenses ("Letters: N") ─────────

var MAILHOUSE_RE=/alpha ?graphics|mail ?house|mailing service|letter ?shop|presort|\beddm\b|every door direct|mail ?shop/i;
var partsCache=new WeakMap();
function cachedParts(ex){var p=partsCache.get(ex);if(!p){p=expenseItemParts(ex);partsCache.set(ex,p);}return p;}
function isPostageOnly(ex,partsOf){
  var parts=(partsOf||cachedParts)(ex);
  return parts.length>0&&parts.every(function(p){return p.type==="Postage"||!p.cents;})&&parts.some(function(p){return p.type==="Postage";});
}
// A mailhouse charge: its vendor / description say so and it is all postage
// (expenseItems types the mailhouse as Postage). USPS / Stamps.com are not.
function isMailhouseRow(ex,partsOf){
  return!!ex&&MAILHOUSE_RE.test(oneLine(ex.vendor)+" "+oneLine(ex.desc))&&isPostageOnly(ex,partsOf);
}
// Which mailhouse a charge is from: the first word of its vendor
// ("AlphaGraphics" = "AlphaGraphics of Gilbert"), else the mailhouse words in
// its description ("mail house" = "mailhouse").
function mailhouseFamily(ex){
  var g=expenseVendorGroup(ex);
  if(g&&g!==NO_VENDOR){var w=oneLine(g).toLowerCase().split(" ")[0].replace(/[^a-z0-9]/g,"");if(w)return w;}
  var m=MAILHOUSE_RE.exec(oneLine(ex&&ex.desc));
  return m?m[0].toLowerCase().replace(/[^a-z]/g,""):"";
}
// Expenses carrying "Letters: N" → drops [{id, letters, rows, days, from,
// to, cents, mailhouse, method, vendor, family}]. A mailhouse drop takes the
// same mailhouse's other charges within 30 days that have no count of
// their own (nearest drop wins) — the AlphaGraphics 6/26 + 7/7 invoices are
// one drop whichever of them carries the count.
function letterDrops(expenses,partsOf){
  partsOf=partsOf||cachedParts;
  var list=(expenses||[]).filter(Boolean);
  var drops=[];var counted=new Set();
  list.forEach(function(ex){
    var n=parseLettersLine(ex.notes);var day=localDay(ex.date);
    if(!n||!day)return;
    counted.add(ex);
    var mh=isMailhouseRow(ex,partsOf);
    drops.push({id:ex.id,letters:n,rows:[ex],days:[day],mailhouse:mh,method:mh?"Mailhouse":"Other",vendor:expenseVendorGroup(ex),family:mailhouseFamily(ex),day:day});
  });
  list.forEach(function(ex){
    if(counted.has(ex)||!isMailhouseRow(ex,partsOf))return;
    var day=localDay(ex.date);if(!day)return;
    var g=mailhouseFamily(ex);var best=null;
    drops.forEach(function(d){
      if(!d.mailhouse||!g||d.family!==g)return;
      var gap=daysApart(day,d.day);
      if(gap<=DROP_WINDOW_DAYS&&(!best||gap<best.gap))best={d:d,gap:gap};
    });
    if(best){best.d.rows.push(ex);best.d.days.push(day);}
  });
  drops.forEach(function(d){
    d.from=d.days.reduce(function(a,b){return b<a?b:a;});
    d.to=d.days.reduce(function(a,b){return b>a?b:a;});
    d.cents=d.rows.reduce(function(s,ex){return s+cents(ex.amount);},0);
  });
  return drops.sort(function(a,b){return a.from<b.from?-1:a.from>b.from?1:0;});
}
function dropText(d){return d.rows.map(function(ex){return oneLine(ex.vendor)+" "+oneLine(ex.desc)+" "+String(ex.notes||"").split("\n").filter(function(l){return!isLettersLine(l);}).join(" ");}).join(" ").toLowerCase();}
// How far a group's mailings sit from the drop's charges (0 = inside them).
function gapToDrop(g,d){
  if(g.to<d.from)return dayNum(d.from)-dayNum(g.to);
  if(g.from>d.to)return dayNum(g.from)-dayNum(d.to);
  return 0;
}
function dropTol(d){return Math.max(10,Math.round(d.letters*0.1));}
// The days a drop's logged mailings can sit on: 7 days before its charges
// to 30 after.
function dropWindow(d){return{from:addDays(d.from,-DROP_LEAD_DAYS),to:addDays(d.to,DROP_WINDOW_DAYS)};}
function dayGap(day,d){return day<d.from?dayNum(d.from)-dayNum(day):day>d.to?dayNum(day)-dayNum(d.to):0;}
// Each drop → already logged as mailings? Every mailing (a group's letters on
// one day = a "unit") is claimed by at most one drop, and a drop by:
//   1. "named"     — every group the charge NAMES (its project / batch /
//                    campaign in the description or notes, or the vendor in
//                    the group's name) within 60 days, whatever its size;
//   2. "mailhouse" — a mailhouse drop: every MAILHOUSE unit (mostly
//                    mailhouse letters — see methodOf) in its window, however
//                    many batches it's split into (the 7/10 AlphaGraphics drop
//                    is four: 500 + 500 + 250 + 250); a unit in two drops'
//                    windows goes to the nearer;
//   3. "count"     — one group in its window with roughly its count (±10%,
//                    at least ±10), the closest count first;
//   4. "day"       — the letters logged on ONE day in its window (every
//                    group still unclaimed) add up to roughly its count.
// A drop claimed by 1 or 2 is logged even when the logged count differs from
// the typed one (shown, never added — the mailings are what's counted).
function matchDrops(drops,groups){
  var units=[];
  groups.forEach(function(g){if(g.key==="letters")return;g.days.forEach(function(x,day){units.push({g:g,day:day,n:x.n,mh:x.mh});});});
  var usedU=new Set(),usedG=new Set();   // claimed units; groups with a claimed unit
  var groupUsed=function(g){return usedG.has(g);};
  var mark=function(d,list,how){
    list.forEach(function(u){usedU.add(u);usedG.add(u.g);});
    var by=new Map();var count=0,from=null,to=null;
    list.forEach(function(u){
      var x=by.get(u.g);if(!x){x={key:u.g.key,label:u.g.label,count:0,from:u.day,to:u.day};by.set(u.g,x);}
      x.count+=u.n;if(u.day<x.from)x.from=u.day;if(u.day>x.to)x.to=u.day;
      count+=u.n;if(!from||u.day<from)from=u.day;if(!to||u.day>to)to=u.day;
    });
    var gs=Array.from(by.values()).sort(function(a,b){return b.count-a.count||(a.label<b.label?-1:1);});
    d.status="logged";
    d.logged={how:how,key:gs[0].key,label:gs[0].label+(gs.length>1?" + "+(gs.length-1)+" more":""),count:count,from:from,to:to,groups:gs};
  };
  var open=function(){return drops.filter(function(d){return!d.status;});};
  // 1. named
  var named=[];
  drops.forEach(function(d){
    var text=dropText(d);var vend=d.vendor&&d.vendor!==NO_VENDOR?d.vendor.toLowerCase():"";
    groups.forEach(function(g){
      if(g.key==="letters"||g.key.indexOf("day:")===0)return;
      var lab=oneLine(g.label).toLowerCase();var gap=gapToDrop(g,d);
      if(lab.length>=4&&gap<=NAMED_WINDOW_DAYS&&(text.indexOf(lab)>=0||(vend.length>=4&&lab.indexOf(vend)>=0)))named.push({d:d,g:g,gap:gap});
    });
  });
  named.sort(function(a,b){return a.gap-b.gap;});
  var namedOf=new Map();var namedG=new Set();
  named.forEach(function(p){if(namedG.has(p.g))return;namedG.add(p.g);var a=namedOf.get(p.d);if(!a){a=[];namedOf.set(p.d,a);}a.push(p.g);});
  namedOf.forEach(function(gs,d){mark(d,units.filter(function(u){return gs.indexOf(u.g)>=0;}),"named");});
  // 2. mailhouse letters in a mailhouse drop's window → the nearest such drop
  var mhDrops=open().filter(function(d){return d.mailhouse;});
  var mhOf=new Map();
  units.forEach(function(u){
    if(usedU.has(u)||!(u.mh*2>u.n))return;
    var best=null;
    mhDrops.forEach(function(d){var w=dropWindow(d);if(!inRange(u.day,w))return;var gap=dayGap(u.day,d);if(!best||gap<best.gap)best={d:d,gap:gap};});
    if(best){var a=mhOf.get(best.d);if(!a){a=[];mhOf.set(best.d,a);}a.push(u);}
  });
  mhOf.forEach(function(list,d){mark(d,list,"mailhouse");});
  // 3. one group of about that count
  var pairs=[];
  open().forEach(function(d){
    var tol=dropTol(d);
    groups.forEach(function(g){
      if(g.key==="letters"||groupUsed(g))return;
      var gap=gapToDrop(g,d);var diff=Math.abs(g.count-d.letters);
      if(gap<=DROP_WINDOW_DAYS&&diff<=tol)pairs.push({d:d,g:g,score:diff*1000+gap});
    });
  });
  pairs.sort(function(a,b){return a.score-b.score;});
  pairs.forEach(function(p){
    if(p.d.status||groupUsed(p.g))return;
    mark(p.d,units.filter(function(u){return u.g===p.g;}),"count");
  });
  // 4. one day's letters adding up to about that count
  open().forEach(function(d){
    var w=dropWindow(d);var tol=dropTol(d);var byDay=new Map();
    units.forEach(function(u){if(usedU.has(u)||!inRange(u.day,w))return;var a=byDay.get(u.day);if(!a){a=[];byDay.set(u.day,a);}a.push(u);});
    var best=null;
    byDay.forEach(function(list,day){
      var n=list.reduce(function(s,u){return s+u.n;},0);var diff=Math.abs(n-d.letters);
      if(diff>tol)return;
      var score=diff*1000+dayGap(day,d);
      if(!best||score<best.score)best={list:list,score:score};
    });
    if(best)mark(d,best.list,"day");
  });
  drops.forEach(function(d){if(!d.status)d.status="added";});
  return drops;
}
// An added drop's letters over its charges' days, in proportion to their
// amounts (all on its last day when none is positive).
function dropLetterDays(d){
  var w=d.rows.map(function(ex){return Math.max(0,cents(ex.amount));});
  if(!w.some(Boolean))return[{day:d.to,n:d.letters}];
  var a=allocate(d.letters,w);var by=new Map();
  d.days.forEach(function(day,i){if(a[i])by.set(day,(by.get(day)||0)+a[i]);});
  return Array.from(by.entries()).map(function(x){return{day:x[0],n:x[1]};});
}

function emptyMethods(){var o={};METHODS.forEach(function(k){o[k]=0;});return o;}
function addLetters(byDay,day,method,n,fromExpenses){
  var x=byDay.get(day);
  if(!x){x={total:0,byMethod:emptyMethods(),fromExpenses:0};byDay.set(day,x);}
  x.total+=n;x.byMethod[method]=(x.byMethod[method]||0)+n;if(fromExpenses)x.fromExpenses+=n;
}
function sumLetters(byDay,r){
  var out={total:0,byMethod:emptyMethods(),fromExpenses:0};
  byDay.forEach(function(x,day){
    if(!inRange(day,r))return;
    out.total+=x.total;out.fromExpenses+=x.fromExpenses;
    METHODS.forEach(function(k){out.byMethod[k]+=x.byMethod[k]||0;});
  });
  return out;
}
// Letters per LOCAL day, and per period. opts: {owner, loans, today,
// partsOf, kind ("month" | "quarter" | "day"), range {from, to}, events (a
// mailingEvents result to reuse — it only changes with the leads)}.
// → {byDay, periods, drops, groups, undated, future, monthOnly, approx,
//    sameDay, total, firstDay, lastDay}
function lettersByPeriod(mktgLeads,expenses,opts){
  opts=opts||{};
  var ev=opts.events||mailingEvents(mktgLeads,opts);
  var groups=mailingGroups(ev.events);
  var drops=matchDrops(letterDrops(expenses,opts.partsOf),groups);
  var byDay=new Map();
  ev.events.forEach(function(e){addLetters(byDay,e.day,e.method,e.n,false);});
  drops.forEach(function(d){
    d.addedDays=d.status==="added"?dropLetterDays(d):[];
    d.addedDays.forEach(function(x){addLetters(byDay,x.day,d.method,x.n,true);});
  });
  var days=Array.from(byDay.keys()).sort();
  var total=0;byDay.forEach(function(x){total+=x.total;});
  var out={byDay:byDay,drops:drops,groups:groups,undated:ev.undated,future:ev.future,monthOnly:ev.monthOnly,approx:ev.approx||0,sameDay:ev.sameDay||0,total:total,firstDay:days[0]||null,lastDay:days[days.length-1]||null};
  var r=opts.range&&opts.range.from&&opts.range.to?opts.range:(days.length?{from:days[0],to:days[days.length-1]}:null);
  out.periods=r?periodsBetween(r.from,r.to,opts.kind||"month").map(function(p){
    var s=sumLetters(byDay,p);return Object.assign({},p,{letters:s.total,byMethod:s.byMethod,fromExpenses:s.fromExpenses});
  }):[];
  return out;
}

// ── Costs ────────────────────────────────────────────────────

// Parcel postage: the charge's vendor / description says Priority Mail,
// Click-N-Ship, tracking … and names no stamps.
function isParcelPostage(ex){
  var t=oneLine(ex&&ex.vendor)+" "+oneLine(ex&&ex.desc);
  return PARCEL_RE.test(t)&&!STAMP_WORDS_RE.test(t);
}
// Every part of every expense that is a component → {day, comp, cents, id,
// units, unitName, vendor (the vendor group), mailhouse (a Postage part of a
// mailhouse charge — isMailhouseRow), family (its mailhouse — mailhouseFamily),
// parcel (a Postage part of parcel postage — isParcelPostage)}. Refunds are
// negative parts and net out.
function costRecords(expenses,partsOf){
  partsOf=partsOf||cachedParts;
  var out=[];
  (expenses||[]).forEach(function(ex){
    if(!ex)return;
    var day=localDay(ex.date);if(!day)return;
    var mh=null,parcel=null;
    partsOf(ex).forEach(function(p){
      var k=COMP_OF_TYPE[p.type];
      if(!k||!p.cents)return;
      if(k==="Postage"&&mh===null){mh=isMailhouseRow(ex,partsOf);parcel=!mh&&isParcelPostage(ex);}
      var r={day:day,comp:k,cents:p.cents,id:ex.id,units:p.units==null?null:p.units,unitName:p.unitName||null,vendor:p.vendor||expenseVendorGroup(ex),mailhouse:k==="Postage"&&!!mh,variety:p.variety||null,kindGuess:!!p.kindGuess};
      if(r.mailhouse)r.family=mailhouseFamily(ex);
      if(k==="Postage"&&parcel)r.parcel=true;
      out.push(r);
    });
  });
  return out;
}
function compKeyFor(k,opts){return k==="Cardstock"&&opts&&opts.cardstockInPaper?"Paper":k;}
function componentKeys(opts){return COMPONENTS.map(function(c){return c.key;}).filter(function(k){return!(k==="Cardstock"&&opts&&opts.cardstockInPaper);});}
function emptyComps(keys){var o={};keys.forEach(function(k){o[k]=0;});return o;}
// Spend per component in a range: {cents:{comp}, ids:{comp:[expense ids]},
// mailhousePostage (the mailhouse's share of Postage, cents)}.
function sumCosts(recs,r,opts){
  var keys=componentKeys(opts);var c=emptyComps(keys);var ids={};keys.forEach(function(k){ids[k]=[];});
  var mh=0;
  recs.forEach(function(x){
    if(!inRange(x.day,r))return;
    var k=compKeyFor(x.comp,opts);
    c[k]+=x.cents;if(ids[k].indexOf(x.id)<0)ids[k].push(x.id);
    if(x.mailhouse)mh+=x.cents;
  });
  return{cents:c,ids:ids,mailhousePostage:mh};
}
// Actual spend per period (no smoothing, cards as bought).
// opts: {kind, range, cardstockInPaper}. → [{key, label, from, to, cents, ids}]
function costsByPeriod(expenses,partsOf,opts){
  opts=opts||{};
  var recs=costRecords(expenses,partsOf);
  var r=opts.range&&opts.range.from&&opts.range.to?opts.range:null;
  if(!r){
    var days=recs.map(function(x){return x.day;}).sort();
    if(!days.length)return[];
    r={from:days[0],to:days[days.length-1]};
  }
  return periodsBetween(r.from,r.to,opts.kind||"month").map(function(p){var s=sumCosts(recs,p,opts);return Object.assign({},p,{cents:s.cents,ids:s.ids});});
}

// Business cards, averaged: all-time card spend ÷ cards bought (the Items
// lines' counts; a refund with a count takes its cards back). Purchases
// with no count are priced at the counted ones' average; with no count at
// all the spend is spread over the letters instead (`spread`). Never more
// than was spent: see cardsCapped.
function cardsAverage(recs){
  var o={cents:0,knownCents:0,cards:0,unknownCents:0,purchases:0};var ids=new Set();
  recs.forEach(function(x){
    if(x.comp!=="Business Cards")return;
    o.cents+=x.cents;ids.add(x.id);
    if(x.units!=null&&x.unitName==="cards"){o.knownCents+=x.cents;o.cards+=(x.cents<0?-1:1)*x.units;}
    else o.unknownCents+=x.cents;
  });
  o.purchases=ids.size;
  o.ids=Array.from(ids);
  o.perCard=o.cards>0&&o.knownCents>0?o.knownCents/100/o.cards:null;   // display only
  o.spread=!o.perCard&&o.cents>0;
  return o;
}
// Cards per letter as tenths (0.5 → 5), 0…100.
function cplTenths(v){var n=Math.round((Number(v==null?1:v)||0)*10);return Math.max(0,Math.min(100,n));}
// More cards needed than were ever bought? `allLetters` (every letter that
// carried a card — mailed on or before cardsUntil, all time) × cards per
// letter vs the cards bought — the purchases with no count included at the
// same average (cards × all spend ÷ counted spend).
// Exact: allLetters × tenths × knownCents > 10 × cards × cents.
function cardsCapped(cards,tenths,allLetters){
  return!!(cards&&cards.perCard&&tenths>0&&allLetters>0&&cards.cents>0&&allLetters*tenths*cards.knownCents>10*cards.cards*cards.cents);
}
// Card cost for `letters` letters, in cents: the average per card × cards
// per letter — or, when that would need more cards than were bought
// (cardsCapped), the card spend spread over all `allLetters` letters, so the
// cards never cost more than was paid for them.
function cardsCentsFor(letters,cards,tenths,allLetters){
  if(!letters||!tenths||!cards)return 0;
  if(cards.perCard){
    if(cardsCapped(cards,tenths,allLetters))return divRound(cards.cents*letters,allLetters);
    return divRound(letters*tenths*cards.knownCents,10*cards.cards);
  }
  if(cards.spread&&allLetters>0)return divRound(cards.cents*letters,allLetters);
  return 0;
}

// ── Stamp check ──────────────────────────────────────────────

// A stamp price setting: whole cents, 1…999, else the default.
function stampPrice(v,def){var n=Number(v);return v!==""&&v!=null&&isFinite(n)&&n===Math.round(n)&&n>=1&&n<=999?n:def;}
// The stamp prices in force: {usps (from USPS_PRICE_FROM — the setting),
// uspsBefore (before it), uspsFrom, stampsCom}.
function stampPrices(opts){
  opts=opts||{};
  return{usps:stampPrice(opts.priceUsps,STAMP_PRICE_USPS),uspsBefore:stampPrice(opts.priceUspsBefore,STAMP_PRICE_USPS_BEFORE),uspsFrom:USPS_PRICE_FROM,stampsCom:stampPrice(opts.priceStampsCom,STAMP_PRICE_STAMPSCOM)};
}
// The cents a stamp cost on `day` from `vendor` (a vendor group).
function stampPriceOn(day,vendor,prices){
  if(vendor===STAMPS_COM)return prices.stampsCom;
  return day<prices.uspsFrom?prices.uspsBefore:prices.usps;
}
// A stamp count on the receipt wins over dollars ÷ price.
function stampsCounted(x){return x.units!=null&&x.units>0&&x.unitName==="stamps";}
// Stamps bought: every Postage part that is NOT the mailhouse's (its postage
// is inside its invoice; its letters are the Mailhouse ones) nor parcel
// postage → [{day, month, cents, stamps, price, stampsCom, counted, id}],
// oldest first. The count: the Items line's stamp count when it has one
// ("Postage $160.00 (10 · 200 stamps)" — the receipt's LETTER stamps; it
// also makes a parcel charge count), else the amount ÷ the stamp price on
// its date, rounded down — USPS and every other store (the post office,
// Fry's, The UPS Store …) 78¢ before 7/13/2026 and 82¢ (the setting) from
// then, Stamps.com 78¢. A refund is a negative count: its stamps go back.
// Postage kinds that are never a letter's stamp (10/11/26): add-on
// denominations and parcels / shipping.
function notLetterPostage(x){return x.variety===POSTAGE_KINDS.ADDON||x.variety===POSTAGE_KINDS.PARCEL;}
// opts.replacedIds (Set / array): hand entries flagged "Replaced by the
// imported USPS/Stamps.com receipts" — the receipts are the record, so their
// stamps aren't counted twice (review F4: the 9/09 typed "Stamps" $820 was
// 1,000 extra September stamps on hand). A row Reece KEPT carries no flag.
function replacedSet(opts){var r=opts&&opts.replacedIds;return r instanceof Set?r:new Set(r||[]);}
function stampPurchases(recs,opts){
  var prices=stampPrices(opts);
  var replaced=replacedSet(opts);
  var out=[];
  (recs||[]).forEach(function(x){
    if(x.comp!=="Postage"||x.mailhouse||!x.cents||replaced.has(x.id))return;
    var counted=stampsCounted(x);
    // add-ons / parcels: a TYPED kind is never letter stamps, whatever count it
    // carries; a kind guessed from a hand row's description yields to the
    // line's letter-stamp count ("Letter stamps it bought")
    if(notLetterPostage(x)&&!(counted&&x.kindGuess))return;
    if(x.parcel&&!counted&&!x.variety)return;   // a typed Letter-stamps part on a parcel receipt still counts
    var sc=x.vendor===STAMPS_COM;var price=stampPriceOn(x.day,x.vendor,prices);
    var n=counted?x.units:Math.floor(Math.abs(x.cents)/price);
    out.push({day:x.day,month:monthKey(x.day),cents:x.cents,stamps:(x.cents<0?-1:1)*n,price:price,stampsCom:sc,counted:counted,id:x.id});
  });
  return out.sort(function(a,b){return a.day<b.day?-1:a.day>b.day?1:0;});
}
// Parcel postage left out of the stamps (no stamp count on it) → {count
// (charges), cents, ids}.
function parcelPostage(recs){
  var o={count:0,cents:0,ids:[]};
  (recs||[]).forEach(function(x){
    if(x.comp!=="Postage"||x.mailhouse||!x.cents)return;
    var kindParcel=x.variety===POSTAGE_KINDS.PARCEL;   // a part of kind parcels (10/11/26)
    if(stampsCounted(x)&&(!kindParcel||x.kindGuess))return;   // a letter-stamp count wins — unless the parcel kind was typed
    if(!kindParcel&&(!x.parcel||x.variety))return;
    o.cents+=x.cents;if(o.ids.indexOf(x.id)<0){o.ids.push(x.id);o.count++;}
  });
  return o;
}
// Put the receipt's letter-stamp count on a postage charge's Items line
// ("Items: Postage $427.23 (471 stamps)"; n = 0 takes it off — dollars ÷
// price again). → the new notes, or null when the Items line can't take it
// (more than one Postage part, or a part with no amount — edit it by hand).
// Every other part of the line is kept.
function withStampCount(ex,n){
  n=Math.max(0,Math.round(Number(n)||0));
  var notes=String((ex&&ex.notes)||"");
  if(!hasItemsLine(notes))return n?withItemsLine(notes,oneItemLine("Postage",ex.amount,null,n,"stamps")):notes;
  var parts=parseItemsLine(notes);
  // The ONE postage part that can be letter stamps: kind Letter stamps,
  // a Stamps.com balance, or no kind (10/11/26 — add-on / parcel parts are
  // never letters, so a "Letter + Add-on" line still takes the count).
  var post=parts.filter(function(p){return p.type==="Postage"&&(!p.variety||p.variety===POSTAGE_KINDS.LETTER||p.variety===POSTAGE_KINDS.BALANCE);});
  if(post.length!==1||parts.some(function(p){return p.amount==null;}))return null;
  if(n){post[0].units=n;post[0].unitName="stamps";}
  else{if(post[0].units==null)return notes;post[0].units=null;}
  return replaceItemsLine(notes,formatItemsLine(parts));
}
function newCount(){return{stamps:0,cents:0,purchases:0};}
// The stamp check — a stamped letter needs a stamp: month by month, all time
// (whatever the view shows), the stamps on hand + the month's purchases back
// the month's stamped letters (every letter logged but the Mailhouse ones),
// oldest stamps first (FIFO cost layers — a stamp never backs a letter in a
// month before it was bought). The letters past the stamps are NOT BACKED:
// left out, and they never take a later month's stamps. A refund takes its
// stamps back (the newest first) and its money off them; money back for
// stamps already used comes off that month's postage (`adjCents`, on the
// refund's day — `adjDays`).
// byDay = lettersByPeriod's. → {months:[{key, label, carriedIn:{stamps,
// cents}, bought:{stamps, cents, purchases}, refunds:{…}, available, logged,
// mailhouse, needing, backed, notBacked, stampCents (what the backed letters'
// stamps cost), adjCents, usedCents (= the two), left:{stamps, cents},
// slices (the stamps the backed letters used, in order: [{n, c}]), adjDays
// ([{day, cents}])}], byMonth, totals of each, onHand (left at the end — in
// no month's figure), short (the months with letters not backed), prices
// (stampPrices), purchases, parcels (parcelPostage — left out)}.
function stampCheck(byDay,recs,opts){
  opts=opts||{};
  var prices=stampPrices(opts);
  var buys=stampPurchases(recs,opts);
  var need=new Map();
  (byDay||new Map()).forEach(function(x,day){
    var k=monthKey(day);var o=need.get(k);if(!o){o={logged:0,mh:0};need.set(k,o);}
    o.logged+=x.total;o.mh+=(x.byMethod&&x.byMethod.Mailhouse)||0;
  });
  var keys=buys.map(function(b){return b.month;}).concat(Array.from(need.keys())).sort();
  var rep=replacedSet(opts);var replaced={count:0,cents:0,ids:[]};
  (recs||[]).forEach(function(x){if(x.comp==="Postage"&&!x.mailhouse&&x.cents&&rep.has(x.id)){replaced.cents+=x.cents;if(replaced.ids.indexOf(x.id)<0){replaced.ids.push(x.id);replaced.count++;}}});
  var out={months:[],byMonth:new Map(),bought:newCount(),refunds:newCount(),logged:0,mailhouse:0,needing:0,backed:0,notBacked:0,stampCents:0,adjCents:0,usedCents:0,onHand:{stamps:0,cents:0},short:[],prices:prices,purchases:buys,parcels:parcelPostage(recs),replaced:replaced};
  if(!keys.length)return out;
  var layers=[];var loose=0;   // FIFO {n stamps, c cents}, oldest first; cents that bought no whole stamp
  var onHand=function(){var s={stamps:0,cents:loose};layers.forEach(function(l){s.stamps+=l.n;s.cents+=l.c;});return s;};
  var bi=0;
  periodsBetween(keys[0]+"-01",monthEnd(keys[keys.length-1]),"month").forEach(function(p){
    var k=p.key;var o=need.get(k)||{logged:0,mh:0};
    var row={key:k,label:p.label,carriedIn:onHand(),bought:newCount(),refunds:newCount(),logged:o.logged,mailhouse:o.mh,needing:o.logged-o.mh,adjCents:0,slices:[],adjDays:[]};
    var mine=[];while(bi<buys.length&&buys[bi].month===k)mine.push(buys[bi++]);
    mine.forEach(function(b){
      if(b.cents<0)return;
      row.bought.stamps+=b.stamps;row.bought.cents+=b.cents;row.bought.purchases++;
      if(b.stamps>0)layers.push({n:b.stamps,c:b.cents});else loose+=b.cents;
    });
    mine.forEach(function(b){
      if(b.cents>=0)return;
      var n=-b.stamps,back=-b.cents;
      row.refunds.stamps+=n;row.refunds.cents+=b.cents;row.refunds.purchases++;
      while(n>0&&layers.length){
        var l=layers[layers.length-1];
        if(l.n<=n){n-=l.n;back-=l.c;layers.pop();}
        else{l.n-=n;l.c-=back;n=0;back=0;}
      }
      row.adjCents-=back;   // what the stamps on hand couldn't take (they were used)
      if(back)row.adjDays.push({day:b.day,cents:-back});
    });
    row.available=onHand().stamps;
    row.backed=Math.max(0,Math.min(row.needing,row.available));
    row.notBacked=row.needing-row.backed;
    var t=row.backed,used=0;
    while(t>0&&layers.length){
      var l=layers[0];var take=Math.min(t,l.n);
      var cost=take===l.n?l.c:divRound(l.c*take,l.n);
      l.n-=take;l.c-=cost;used+=cost;t-=take;
      row.slices.push({n:take,c:cost});
      if(!l.n){loose+=l.c;layers.shift();}
    }
    row.stampCents=used;
    row.usedCents=used+row.adjCents;
    row.left=onHand();
    out.months.push(row);out.byMonth.set(k,row);
    ["stamps","cents","purchases"].forEach(function(f){out.bought[f]+=row.bought[f];out.refunds[f]+=row.refunds[f];});
    ["logged","mailhouse","needing","backed","notBacked","stampCents","adjCents","usedCents"].forEach(function(f){out[f]+=row[f];});
    if(row.notBacked>0)out.short.push(k);
  });
  out.onHand=onHand();
  return out;
}
// What the first k stamps a month's backed letters used cost (its FIFO
// slices in order; a cut slice pro rata, rounded — usedUpTo(row, backed) is
// its stampCents exactly).
function usedUpTo(row,k){
  var c=0;var sl=row.slices||[];
  for(var i=0;i<sl.length&&k>0;i++){var s=sl[i];if(k>=s.n){c+=s.c;k-=s.n;}else{c+=divRound(s.c*k,s.n);k=0;}}
  return c;
}
// The part of the stamp check inside a range: {needing, backed, usedCents}.
// Whole months as they are. A month the range cuts is walked in DAY ORDER:
// its backed letters are the first ones logged that month (first come, first
// served from the month's stamps — on hand + bought that month), so the range
// gets the backed letters that fall inside it and what their stamps cost
// (the same FIFO order), plus a stamp refund's money on a day inside it.
// Ranges that split a month add up to the month exactly.
function stampShare(sc,byDay,r){
  var out={needing:0,backed:0,usedCents:0};
  if(!sc)return out;
  sc.months.forEach(function(row){
    var mFrom=row.key+"-01",mTo=monthEnd(row.key);
    if(r&&((r.to&&mFrom>r.to)||(r.from&&mTo<r.from)))return;
    if(!r||((!r.from||r.from<=mFrom)&&(!r.to||r.to>=mTo))){out.needing+=row.needing;out.backed+=row.backed;out.usedCents+=row.usedCents;return;}
    var before=0,inN=0;
    byDay.forEach(function(x,day){
      if(day<mFrom||day>mTo)return;
      var n=x.total-((x.byMethod&&x.byMethod.Mailhouse)||0);
      if(r.from&&day<r.from)before+=n;else if(inRange(day,r))inN+=n;
    });
    var a=Math.min(row.backed,before),b=Math.min(row.backed,before+inN);
    out.needing+=inN;
    out.backed+=b-a;
    out.usedCents+=usedUpTo(row,b)-usedUpTo(row,a)+(row.adjDays||[]).reduce(function(s,x){return s+(inRange(x.day,r)?x.cents:0);},0);
  });
  return out;
}
// The mailhouse's postage, placed in the days its letters went out (used
// when letters are checked against stamps — its invoices can be charged
// weeks before the drop: AlphaGraphics 6/26 + 7/07 for the 7/10 letters).
// Its charges are grouped like letterDrops' (the same mailhouse — family —
// each within 30 days of the group's last charge); each group's cents are
// spread (allocate, exact) over the Mailhouse letters logged from 7 days
// before its first charge to 30 after its last (dropWindow — a day in two
// groups' windows goes to the nearer); a group with no Mailhouse letters
// there stays on its charges' days, as bought.
// → {byDay: Map(day → cents), groups:[{family, from, to, cents, ids,
//    letters, placed, letterFrom, letterTo, byDay}]} — a group's own byDay
//    is ITS cents only (two groups' windows can overlap — a monthly drop —
//    so the combined byDay is never filtered by a group's window).
function mailhousePlacement(recs,byDay){
  var mh=(recs||[]).filter(function(x){return x.comp==="Postage"&&x.mailhouse&&x.cents;}).slice().sort(function(a,b){return a.day<b.day?-1:a.day>b.day?1:0;});
  var groups=[];
  mh.forEach(function(x){
    var fam=x.family||"";var g=null;
    for(var i=groups.length-1;i>=0;i--){if(groups[i].family===fam&&daysApart(x.day,groups[i].to)<=DROP_WINDOW_DAYS){g=groups[i];break;}}
    if(!g){g={family:fam,from:x.day,to:x.day,cents:0,ids:[],recs:[]};groups.push(g);}
    g.recs.push(x);g.cents+=x.cents;if(x.day>g.to)g.to=x.day;if(g.ids.indexOf(x.id)<0)g.ids.push(x.id);
  });
  var daysOf=new Map();
  (byDay||new Map()).forEach(function(x,day){
    var n=(x.byMethod&&x.byMethod.Mailhouse)||0;if(!n)return;
    var best=null;
    groups.forEach(function(g){if(!inRange(day,dropWindow(g)))return;var gap=dayGap(day,g);if(!best||gap<best.gap)best={g:g,gap:gap};});
    if(best){var a=daysOf.get(best.g);if(!a){a=[];daysOf.set(best.g,a);}a.push({day:day,n:n});}
  });
  var placed=new Map();
  var add=function(day,c){if(c)placed.set(day,(placed.get(day)||0)+c);};
  groups.forEach(function(g){
    var ds=(daysOf.get(g)||[]).sort(function(a,b){return a.day<b.day?-1:a.day>b.day?1:0;});
    g.letters=ds.reduce(function(t,d){return t+d.n;},0);
    g.placed=g.letters>0;
    g.byDay=new Map();
    var mine=function(day,c){if(c)g.byDay.set(day,(g.byDay.get(day)||0)+c);add(day,c);};
    if(!g.placed){g.recs.forEach(function(x){mine(x.day,x.cents);});return;}
    g.letterFrom=ds[0].day;g.letterTo=ds[ds.length-1].day;
    var a=allocate(g.cents,ds.map(function(d){return d.n;}));
    ds.forEach(function(d,i){mine(d.day,a[i]);});
  });
  groups.forEach(function(g){delete g.recs;});
  return{byDay:placed,groups:groups};
}
// The mailhouse postage placed inside a range (mailhousePlacement's byDay).
function placedIn(placement,r){
  var c=0;if(!placement)return c;
  placement.byDay.forEach(function(v,day){if(inRange(day,r))c+=v;});
  return c;
}
// The letters counted in a range: as logged (sc null), or checked against
// stamps — the Mailhouse letters + the backed ones (the methods of the
// stamped letters scaled to the backed count, exactly). → {total, byMethod,
// fromExpenses, logged, notBacked, usedCents}.
function countedLetters(L,sc,r){
  var s=sumLetters(L.byDay,r);
  if(!sc)return{total:s.total,byMethod:s.byMethod,fromExpenses:s.fromExpenses,logged:s.total,notBacked:0,usedCents:0};
  var sh=stampShare(sc,L.byDay,r);
  var bm=emptyMethods();bm.Mailhouse=s.byMethod.Mailhouse||0;
  var stamped=METHODS.filter(function(k){return k!=="Mailhouse";});
  var a=allocate(sh.backed,stamped.map(function(k){return s.byMethod[k]||0;}));
  stamped.forEach(function(k,i){bm[k]=a[i];});
  return{total:bm.Mailhouse+sh.backed,byMethod:bm,fromExpenses:s.fromExpenses,logged:s.total,notBacked:sh.needing-sh.backed,usedCents:sh.usedCents};
}
// The letters in a range that carried a business card: the counted ones
// mailed on or before cardsUntil (null = every one).
function cardLettersIn(L,sc,cardsUntil,r){
  r=r||{from:"",to:""};
  if(cardsUntil){
    if(r.from&&r.from>cardsUntil)return 0;
    r={from:r.from||"",to:!r.to||r.to>cardsUntil?cardsUntil:r.to};
  }
  return countedLetters(L,sc,r).total;
}
// The biggest logged day in the months the stamps can't back (review F3): a
// sync that dates a batch of letters with one placeholder day (the 8/20 v275
// sync — 7,877 letters: July's, mid-September's and repeats) shows up as one
// day carrying a big share of its month. Capping the month can't move those
// letters, so the stamp check names that day. → {day, n (its stamped letters),
// month, monthName, logged (the month's)}, or null when no day in those months
// carries a fifth of its month.
function stampPeakDay(sc,byDay){
  if(!sc||!sc.short||!sc.short.length)return null;
  var months={};sc.short.forEach(function(k){months[k]=1;});
  var best=null;
  (byDay||new Map()).forEach(function(x,day){var k=monthKey(day);if(!months[k])return;var n=x.total-((x.byMethod&&x.byMethod.Mailhouse)||0);if(!best||n>best.n||(n===best.n&&day<best.day))best={day:day,n:n,month:k};});
  if(!best)return null;
  var row=sc.byMonth.get(best.month);var logged=row?row.needing:0;
  if(!logged||best.n*5<logged)return null;
  return Object.assign(best,{monthName:MONTHS_LONG[+best.month.slice(5,7)-1],logged:logged});
}
function monthLong(key,today){var y=key.slice(0,4);return MONTHS_LONG[+key.slice(5,7)-1]+(today&&String(today).slice(0,4)===y?"":" "+y);}
// One line for the stamp check: "The mailing log has 1,866 more stamped
// letters through August than the stamps bought could mail — those aren't
// counted." (basis "logged": "— “As logged” counts them anyway.").
function stampHeadline(sc,basis,today){
  if(!sc||!sc.months.length)return"";
  if(!sc.notBacked)return"Every stamped letter in the mailing log is backed by stamps bought"+(sc.onHand.stamps>0?" — "+fmtInt(sc.onHand.stamps)+" "+(sc.onHand.stamps===1?"stamp":"stamps")+" left over":"")+".";
  var last=sc.short[sc.short.length-1];
  var t="The mailing log has "+fmtInt(sc.notBacked)+" more stamped "+(sc.notBacked===1?"letter":"letters")+" through "+monthLong(last,today)+" than the stamps bought could mail";
  if(sc.short.length>1)t+=" ("+sc.short.map(function(k){return MONTHS_SHORT[+k.slice(5,7)-1]+" "+fmtInt(sc.byMonth.get(k).notBacked);}).join(" · ")+")";
  return t+(basis==="logged"?" — “As logged” counts them anyway.":" — those aren't counted.");
}

// ── Per letter ───────────────────────────────────────────────

// periodCosts {comp: cents} + letters → {letters, cents, perLetter (cents,
// null with no letters), comps:{comp:{cents, perLetter}}}; the components'
// per-letter cents add up to perLetter exactly.
function costPerLetter(periodCosts,periodLetters){
  var keys=Object.keys(periodCosts||{});
  var n=Math.max(0,Math.round(Number(periodLetters)||0));
  var vals=keys.map(function(k){return Math.round(periodCosts[k]||0);});
  var total=vals.reduce(function(s,v){return s+v;},0);
  var per=n?apportion(vals,n):vals.map(function(){return null;});
  var comps={};keys.forEach(function(k,i){comps[k]={cents:vals[i],perLetter:per[i]};});
  return{letters:n,cents:total,perLetter:n?divRound(total,n):null,comps:comps};
}

// "+$0.62 per letter, 48% more" — b against a, both {cents, letters}.
// The difference is the two per-letter figures ON SCREEN (shownA / shownB
// when given — a compare row shows the apportioned component figures —
// else each total rounded), so the three numbers on screen agree and the
// rows add up to the total's difference; the % is exact.
function diffInfo(a,b,shownA,shownB){
  var pa=!a.letters?null:shownA!=null?shownA:divRound(a.cents,a.letters);
  var pb=!b.letters?null:shownB!=null?shownB:divRound(b.cents,b.letters);
  if(pa==null||pb==null)return{diff:null,pct:null,text:pa==null&&pb==null?"no letters in either":pa==null?"no letters before":"no letters after"};
  var diff=pb-pa;
  var pct=a.cents>0?divRound(100*(b.cents*a.letters-a.cents*b.letters),a.cents*b.letters):null;
  var text;
  if(diff===0)text=pct?"about the same per letter ("+(pct>0?"+":"")+pct+"%)":"the same per letter";
  else{
    text=(diff>0?"+":"-")+fmtMoneyCents(Math.abs(diff))+" per letter";
    if(pct!=null&&pct!==0)text+=", "+Math.abs(pct)+"% "+(pct>0?"more":"less");
    else if(pa===0&&diff>0)text+=" (none before)";
  }
  return{diff:diff,pct:pct,text:text};
}

// ── The whole picture (the screen's model) ───────────────────

// mktgLeads, expenses → everything the Cost per letter section draws.
// opts: {owner, loans, today, partsOf, kind ("month" | "quarter"), range
// ({from, to} — a custom span; otherwise the first letter / purchase …
// today), smoothed, cardstockInPaper, cardsPerLetter (default 1), cardsUntil
// (the last day a card went in the envelope — default CARDS_UNTIL_DEFAULT;
// "" / null = every letter), basis ("stamps" — checked against stamps, the
// default — or "logged"), priceUsps (cents a USPS / store stamp from
// 7/13/2026, 82) / priceUspsBefore (before it, 78) / priceStampsCom (78),
// letters (a lettersByPeriod result to reuse — the slow part, every lead's
// mailings)}.
function buildCostPerLetter(mktgLeads,expenses,opts){
  opts=Object.assign({kind:"month",smoothed:false,cardstockInPaper:false,cardsPerLetter:1},opts||{});
  if(opts.cardsUntil===undefined)opts.cardsUntil=CARDS_UNTIL_DEFAULT;
  opts.basis=opts.basis==="logged"?"logged":"stamps";
  opts.priceUsps=stampPrice(opts.priceUsps,STAMP_PRICE_USPS);
  opts.priceStampsCom=stampPrice(opts.priceStampsCom,STAMP_PRICE_STAMPSCOM);
  opts.priceUspsBefore=stampPrice(opts.priceUspsBefore,STAMP_PRICE_USPS_BEFORE);
  var cardsUntil=isDay(opts.cardsUntil)?opts.cardsUntil:null;
  var today=opts.today||todayLocal();
  var L=opts.letters||lettersByPeriod(mktgLeads,expenses,{owner:opts.owner,loans:opts.loans,today:today,partsOf:opts.partsOf});
  var recs=costRecords(expenses,opts.partsOf);
  var cards=cardsAverage(recs);
  var tenths=cplTenths(opts.cardsPerLetter);
  var keys=componentKeys(opts);
  // The stamp check runs all time whatever the view (shown in both bases);
  // checked against stamps, it decides which letters count.
  var stamps=stampCheck(L.byDay,recs,opts);
  var sc=opts.basis==="stamps"?stamps:null;
  // …and the mailhouse's invoices go in the month its letters went out.
  var mhPlace=mailhousePlacement(recs,L.byDay);
  // The span: a custom range, else the first letter or component purchase … today.
  var span;
  if(opts.range&&isDay(opts.range.from)&&isDay(opts.range.to)&&opts.range.from<=opts.range.to)span={from:opts.range.from,to:opts.range.to,custom:true};
  else{
    var firsts=[L.firstDay].concat(recs.map(function(x){return x.day;})).filter(Boolean).sort();
    var last=[L.lastDay,today].concat(recs.map(function(x){return x.day;})).filter(Boolean).sort().pop();
    span=firsts.length?{from:firsts[0],to:last>today?last:today,custom:false}:null;
  }
  var stop=stopInfo(L.byDay);
  var all=countedLetters(L,sc,null);
  // Cards: the average for the letters that carried one (mailed on or before
  // cardsUntil), capped at what was bought over EVERY such letter, all time —
  // a custom range gets its share; letters after cardsUntil get none.
  var cardLetters=cardLettersIn(L,sc,cardsUntil,null);
  var model={opts:opts,today:today,keys:keys,cards:cards,cardsTenths:tenths,cardsUntil:cardsUntil,basis:opts.basis,stamps:stamps,mailhouse:mhPlace,letters:L,recs:recs,span:span,stop:stop,periods:[],totals:null,compareDefaults:defaultCompareRanges(L.byDay),
    allLetters:all.total,loggedLetters:all.logged,notBacked:all.notBacked,cardLetters:cardLetters,cardsCapped:cardsCapped(cards,tenths,cardLetters)};
  if(!span)return model;
  var spanL=countedLetters(L,sc,span);
  var spanCost=sumCosts(recs,span,opts);
  model.spanLetters=spanL.total;
  model.spanCost=spanCost;
  model.spanMailhouse=sc?placedIn(mhPlace,span):spanCost.mailhousePostage;
  var periods=periodsBetween(span.from,span.to,opts.kind).map(function(p){
    var s=countedLetters(L,sc,p);var c=sumCosts(recs,p,opts);
    return Object.assign({},p,{letters:s.total,byMethod:s.byMethod,fromExpenses:s.fromExpenses,logged:s.logged,notBacked:s.notBacked,usedCents:s.usedCents,
      mailhousePostage:sc?placedIn(mhPlace,p):c.mailhousePostage,mailhouseBought:c.mailhousePostage,
      cardLetters:cardLettersIn(L,sc,cardsUntil,p),spent:c.cents,ids:c.ids});
  });
  var weights=periods.map(function(p){return p.letters;});
  // Shown cents per component per period: as bought (postage checked against
  // stamps: the stamps the letters used + the mailhouse in the period its
  // letters went out); smoothed
  // supplies and the averaged cards are the span's figure spread over the
  // periods by their letters (so the periods add up to the span exactly).
  var shown=periods.map(function(p){var o={};keys.forEach(function(k){o[k]=p.spent[k];});if(sc)o.Postage=p.usedCents+p.mailhousePostage;return o;});
  var smoothedKeys=keys.filter(function(k){return opts.smoothed&&COMP_INFO[k].supply&&!COMP_INFO[k].averaged;});
  var unallocated={};
  smoothedKeys.forEach(function(k){
    var a=allocate(spanCost.cents[k],weights);
    periods.forEach(function(p,i){shown[i][k]=a[i];});
    if(!spanL.total&&spanCost.cents[k])unallocated[k]=spanCost.cents[k];
  });
  var spanCards=cardsCentsFor(cardLettersIn(L,sc,cardsUntil,span),cards,tenths,cardLetters);
  var ca=allocate(spanCards,periods.map(function(p){return p.cardLetters;}));
  periods.forEach(function(p,i){shown[i]["Business Cards"]=ca[i];});
  periods.forEach(function(p,i){
    var cpl=costPerLetter(shown[i],p.letters);
    p.shown=shown[i];p.cost=cpl;
    p.spentTotal=keys.reduce(function(s,k){return s+p.spent[k];},0);
    p.afterStop=!!(stop.stopDay&&p.from>stop.stopDay);
    p.note=periodNote(p);
  });
  model.periods=periods;
  var spanShown={};keys.forEach(function(k){spanShown[k]=periods.reduce(function(s,p){return s+p.shown[k];},0);});
  // spentTotal = the components as bought (cards: the purchases in the
  // span; postage: every stamp bought) — what was actually paid; cost.cents
  // = what the per-letter figures use (cards averaged, supplies smoothed,
  // postage = the stamps used + the mailhouse where its letters went, when
  // checked against stamps).
  var spentTotal=keys.reduce(function(s,k){return s+spanCost.cents[k];},0);
  model.totals={letters:spanL.total,byMethod:spanL.byMethod,fromExpenses:spanL.fromExpenses,logged:spanL.logged,notBacked:spanL.notBacked,spent:spanCost.cents,spentTotal:spentTotal,shown:spanShown,cost:costPerLetter(spanShown,spanL.total),unallocated:unallocated};
  model.smoothedKeys=smoothedKeys;
  return model;
}
// A period's note: "no letters" (spend, no letters, up to the last mailing
// month), "after the last mailing month — spend continues", "few letters
// after the last mailing month — spend continues", and — checked against
// stamps — "1,866 logged letters not backed by stamps".
function periodNote(p){
  var nb=p.notBacked||0;
  var base=!p.letters?(nb?"":p.spentTotal||p.cost.cents?(p.afterStop?"after the last mailing month — spend continues":"no letters"):""):(p.afterStop?"few letters after the last mailing month — spend continues":"");
  var nbText=nb?fmtInt(nb)+" logged "+(nb===1?"letter":"letters")+" not backed by stamps":"";
  return base&&nbText?base+" · "+nbText:base||nbText;
}

// The last mailing month: the last month with at least 10 letters logged
// (a stray New Call letter later doesn't restart them) and its last letter.
// (Named "stop" from before 10/10 — it is when the LOGGED letters end.)
function stopInfo(byDay){
  var months=new Map();
  byDay.forEach(function(x,day){var k=monthKey(day);months.set(k,(months.get(k)||0)+x.total);});
  var sig=Array.from(months.keys()).filter(function(k){return months.get(k)>=STOP_MIN_LETTERS;}).sort();
  if(!sig.length)return{stopMonth:null,stopDay:null,lastLetterDay:null,months:months};
  var stopMonth=sig[sig.length-1];
  var lastInStop=null;byDay.forEach(function(x,day){if(monthKey(day)===stopMonth&&x.total&&(!lastInStop||day>lastInStop))lastInStop=day;});
  var lastAny=null;byDay.forEach(function(x,day){if(x.total&&(!lastAny||day>lastAny))lastAny=day;});
  return{stopMonth:stopMonth,stopDay:monthEnd(stopMonth),lastLetterDay:lastInStop,lastAnyDay:lastAny,months:months,significant:sig};
}
// Compare's defaults: the first 3 months with letters vs the last 3 months
// with letters up to the last mailing month (with fewer than 6 such months,
// the first and last half). Months with letters = at least 10 letters logged
// (STOP_MIN_LETTERS).
function defaultCompareRanges(byDay){
  var s=stopInfo(byDay);var ms=s.significant||[];
  var n=ms.length;
  if(!n)return null;
  var k=n>=6?3:Math.max(1,Math.floor(n/2));
  if(n===1)return{a:{from:ms[0]+"-01",to:monthEnd(ms[0])},b:{from:ms[0]+"-01",to:monthEnd(ms[0])},months:ms};
  return{a:{from:ms[0]+"-01",to:monthEnd(ms[k-1])},b:{from:ms[n-k]+"-01",to:monthEnd(ms[n-1])},months:ms};
}
// One date range → {from, to, letters, byMethod, logged, notBacked, cents
// (shown, per component), spent (as bought), cost (costPerLetter)} under the
// model's settings: the letters counted its way (checked against stamps: the
// backed ones + the mailhouse's — a month the range cuts is walked in day
// order), postage the stamps they used (+ the mailhouse placed where its
// letters went), smoothed
// supplies at the SPAN's rate (span supply spend × these letters ÷ span
// letters), the cards averaged over its letters through cardsUntil.
function rangeStats(range,model){
  var opts=model.opts;var keys=model.keys;
  var sc=model.basis==="stamps"?model.stamps:null;
  var s=countedLetters(model.letters,sc,range);
  var c=sumCosts(model.recs,range,opts);
  var shown={};keys.forEach(function(k){shown[k]=c.cents[k];});
  if(sc)shown.Postage=s.usedCents+placedIn(model.mailhouse,range);
  (model.smoothedKeys||[]).forEach(function(k){shown[k]=model.spanLetters?divRound(model.spanCost.cents[k]*s.total,model.spanLetters):0;});
  var cl=cardLettersIn(model.letters,sc,model.cardsUntil,range);
  shown["Business Cards"]=cardsCentsFor(cl,model.cards,model.cardsTenths,model.cardLetters||cl);
  return{from:range.from,to:range.to,letters:s.total,byMethod:s.byMethod,fromExpenses:s.fromExpenses,logged:s.logged,notBacked:s.notBacked,spent:c.cents,ids:c.ids,shown:shown,cost:costPerLetter(shown,s.total)};
}
// Two ranges side by side → {a, b, rows:[{key, label, a, b (per-letter
// cents, as shown — they add up to the totals), diff, pct, text}], total:{…}}.
function compare(rangeA,rangeB,model){
  var a=rangeStats(rangeA,model),b=rangeStats(rangeB,model);
  var rows=model.keys.map(function(k){
    var d=diffInfo({cents:a.shown[k],letters:a.letters},{cents:b.shown[k],letters:b.letters},a.cost.comps[k].perLetter,b.cost.comps[k].perLetter);
    return{key:k,label:COMP_INFO[k].label,a:a.cost.comps[k].perLetter,b:b.cost.comps[k].perLetter,aCents:a.shown[k],bCents:b.shown[k],diff:d.diff,pct:d.pct,text:d.text};
  });
  var t=diffInfo({cents:a.cost.cents,letters:a.letters},{cents:b.cost.cents,letters:b.letters});
  return{a:a,b:b,rows:rows,total:{key:"Total",label:"Total",a:a.cost.perLetter,b:b.cost.perLetter,diff:t.diff,pct:t.pct,text:t.text}};
}

export {
  COMPONENTS, METHODS, HANDWRITTEN_LETTER, DROP_WINDOW_DAYS, DROP_LEAD_DAYS, STOP_MIN_LETTERS, LETTERS_PREFIX,
  CARDS_UNTIL_DEFAULT, STAMP_PRICE_USPS, STAMP_PRICE_USPS_BEFORE, USPS_PRICE_FROM, STAMP_PRICE_STAMPSCOM,
  // the notes line
  isLettersLine, parseLettersLine, formatLettersLine, withLettersLine,
  // letters
  methodOf, mailingDay, mailingEvents, mailingGroups, letterDrops, matchDrops, isMailhouseRow, lettersByPeriod, dropLetterDays, dropWindow, monthKey, notLetterPostage,
  // costs
  costRecords, costsByPeriod, sumCosts, cardsAverage, cardsCapped, cardsCentsFor, cplTenths, componentKeys,
  // the stamp check
  stampPrice, stampPrices, stampPriceOn, stampPurchases, parcelPostage, isParcelPostage, withStampCount, stampCheck, stampShare, usedUpTo, mailhousePlacement, placedIn, countedLetters, cardLettersIn, stampHeadline, stampPeakDay,
  // per letter
  costPerLetter, diffInfo, buildCostPerLetter, periodNote, stopInfo, defaultCompareRanges, rangeStats, compare,
  // periods + exact math + formatting
  periodsBetween, periodKeyOf, periodBounds, periodLabel, monthEnd, allocate, apportion, divRound, fmtMoneyCents, fmtInt, fmtDay, addDays, isDay
};
